import React, { useEffect, useState, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { usePreventWindowUnload, useQuery } from '@panel/commons/hooks';
import { getDevice } from '@panel/commons/helpers';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { DescriptionSection } from './DescriptionSection';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { DescriptionSelectors, DescriptionOperations } from '../../../ducks/description';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { replaceBulletPoints } from './cleanText';

const siteId = process.env.REACT_APP_BUILD_SITE;

const Description = () => {
  const device = getDevice();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const {
    formValues,
    formValuesCompleted,
    formValuesLoading,
    formValuesError,
    suggestedDataError,
    suggestedDataLoading,
    suggestedDataCompleted
  } = useSelector(DescriptionSelectors.getDescriptionState);
  const { snackAction } = useSelector(StepperSelectors.getStepperState);
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState(false);
  const { setFormIsDirty } = useFormIsDirty();
  const [hasInitializedForm, setHasInitializedForm] = useState(false);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);
  const prevTitleError = useRef(titleError);
  const prevDescriptionError = useRef(descriptionError);

  const { register, handleSubmit, errors, formState, control, watch, setValue, clearErrors, trigger } = useForm({
    defaultValues: formValues,
    mode: 'onChange'
  });

  const showTips = !formValuesLoading
    ? [t('professionalPubli.tip.description.title'), t('professionalPubli.tip.description')]
    : [];

  usePreventWindowUnload(formState.isDirty);

  // Observa los valores de los campos en tiempo real
  const title = useWatch({ control, name: 'title' });
  const description = useWatch({ control, name: 'description' });
  const code = useWatch({ control, name: 'code' });

  /**
   * Maneja cambios en los campos del formulario y elimina los errores del form.
   * @param {string} fieldName - Nombre del campo que cambió
   * @param {*} value - Nuevo valor del campo
   */
  const handleFieldChange = (fieldName, value) => {
    setValue(fieldName, value || '', { shouldDirty: true });
    clearErrors(fieldName);
  };

  // Hacemos el fetch de sugerencias de IA para título y descripción
  const handleSuggester = e => {
    e.preventDefault();
    if (!suggestedDataError) {
      dispatch(DescriptionOperations.getSuggestedData(postingId, siteId));
    }
  };

  useEffect(() => {
    if (
      postingId &&
      !formValues.title &&
      !formValuesCompleted &&
      !formValuesLoading &&
      !formValuesError &&
      !titleError &&
      !descriptionError &&
      !internalCodeError &&
      !hasFetchedInitialData
    ) {
      // TODO: Ver como hacer para no depender del setHasFetchedInitialData
      dispatch(DescriptionOperations.getFormValues(postingId));
      setHasFetchedInitialData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, formValues.title, formValuesCompleted, formValuesLoading, formValuesError]);

  useEffect(() => {
    if (
      formValues &&
      formValues.title?.trim() &&
      formValuesCompleted &&
      !formValuesError &&
      !titleError &&
      !descriptionError &&
      !internalCodeError &&
      !hasInitializedForm
    ) {
      setValue('title', formValues.title);
      setValue('description', formValues.description);
      setValue('code', formValues.code);
      // Evita que los formValues sobreescriban los valores del formulario luego de la carga inicial.
      setHasInitializedForm(true);
    }
  }, [
    formValues,
    formValuesCompleted,
    formValuesError,
    titleError,
    descriptionError,
    internalCodeError,
    setValue,
    hasInitializedForm
  ]);

  /**
   * Maneja el envío del formulario para el paso de descripción.
   *
   * @param {Object} data - Datos del formulario enviados por el usuario.
   * @param {string} data.title - Título del aviso (mínimo 10 caracteres).
   * @param {string} data.description - Descripción del aviso (mínimo 150 caracteres).
   * @param {string} [data.code] - Código interno del aviso (opcional).
   */
  const onSubmit = data => {
    if (!titleError && !descriptionError && !internalCodeError) {
      // Actualizamos los valores del formulario en el store
      dispatch(DescriptionOperations.updateFormValues(data));
      setValue('title', data.title);
      setValue('description', data.description);
      setValue('code', data.code);

      // Guardamos los valores en la base de datos
      const payload = {
        body: {
          postingId: postingId,
          title: replaceBulletPoints(data.title),
          description: replaceBulletPoints(data.description),
          internal_code: data.code
        },
        step: 'STEP_DESCRIPTION'
      };
      dispatch(DescriptionOperations.resetSuggestedData()); // Reseteamos suggested data al hacer submit
      dispatch(SavePostingOperations.savePostingDB(payload));
    }
  };

  useEffect(() => {
    if (errors && errors.title) setTitleError(errors.title.type);
    if (errors && errors.description) setDescriptionError(errors.description.type);
    if (errors && errors.code) setInternalCodeError(errors.code.type);
  }, [errors]);

  // Efecto para snackbar de error para validaciones de campos requeridos
  useEffect(() => {
    const titleIsRequired = titleError === 'required' && prevTitleError.current !== 'required';
    const descriptionIsRequired = descriptionError === 'required' && prevDescriptionError.current !== 'required';

    if (titleIsRequired || descriptionIsRequired) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'agregarActionEmpty',
          result: 'ERROR',
          component: t('professionalPubli.location.snackbar.error.generic'),
          section: 'Publicador profesionales - Descripción',
          preventDuplicateActions: true,
          preventStacking: true
        })
      );
    }

    // Actualizamos las refs para próxima comparación cuando cambien los errores de title o description
    prevTitleError.current = titleError;
    prevDescriptionError.current = descriptionError;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleError, descriptionError]);

  // Efecto para snackbar de error cuando falla el fetch de sugerencias de IA o si no hay datos sugeridos.
  useEffect(() => {
    if (suggestedDataError || (suggestedDataCompleted && !formValues.titleAI && !formValues.descriptionAI)) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'suggestedAIError',
          result: 'ERROR',
          component: t('professionalPubli.description.AI.errorMessage'),
          section: 'Publicador profesionales - Descripción',
          preventDuplicateActions: true,
          preventStacking: true
        })
      );
      dispatch(DescriptionOperations.getSuggestedData(null, null, true)); // Reset de suggested data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedDataError, suggestedDataCompleted]);

  useEffect(() => {
    return () => {
      // Saneamiento del state que dispara el Snackbar:
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  useEffect(() => {
    // TODO: nunca cambia el dirty porque?
    setFormIsDirty(formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty]);

  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  return (
    <WrapperContainer
      id='description-step'
      onSubmit={handleSubmit(onSubmit)}
      title={t('professionalPubli.description.title')}
      note={t('professionalPubli.description.note')}
      formValuesLoading={formValuesLoading}
      loadError={formValuesError}
      resetInfo={() => dispatch(DescriptionOperations.resetInfo())}
      getIsValidForm={getIsValidForm}
      tips={showTips}
      disabledSubmit={suggestedDataLoading}
    >
      <form id='description-step'>
        <fieldset disabled={false}>
          {formValues && !formValuesLoading && (
            <DescriptionSection
              device={device}
              register={register}
              setValue={handleFieldChange}
              watch={watch}
              clearErrors={clearErrors}
              errors={errors}
              setTitleError={setTitleError}
              setDescriptionError={setDescriptionError}
              setInternalCodeError={setInternalCodeError}
              titleError={titleError}
              descriptionError={descriptionError}
              internalCodeError={internalCodeError}
              title={title}
              description={description}
              code={code}
              data-publi='step-description-container'
              titleAI={formValues.titleAI}
              descriptionAI={formValues.descriptionAI}
              suggestedDataLoading={suggestedDataLoading}
              suggestedDataError={suggestedDataError}
              handleSuggester={handleSuggester}
            />
          )}
        </fieldset>
      </form>
    </WrapperContainer>
  );
};

export default Description;
